import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { mensajeDeError } from '../lib/mensajeDeError';
import { leer as leerLocal, escribir as escribirLocal } from '../lib/almacenLocal';

// Si la vuelta del pago no se pudo confirmar, durante un rato no se le ofrece pagar otra vez:
// lo más probable es que el pago esté hecho y el aviso de Stripe esté al llegar.
const CLAVE_CONFIRMANDO = 'rutina_mes_confirmando_pago';
const MINUTOS_CONFIRMANDO = 30;
const confirmando = (userId) => {
    const desde = Number(leerLocal(CLAVE_CONFIRMANDO, userId) || 0);
    return desde > 0 && Date.now() - desde < MINUTOS_CONFIRMANDO * 60 * 1000;
};

// LA RUTINA DEL MES QUE MARCÓ EN SU REPORTE Y TODAVÍA NO HA PAGADO (04-10).
//
// Desde que la app no carga la tarjeta guardada, quien marca «Sí» en el reporte mensual la
// paga él, en la pantalla de pago de Stripe. Y la paga desde Inicio, porque la pantalla de
// Rutina no se le abre hasta que la compra. Este hook sabe tres cosas: si tiene una por
// pagar, cómo llevarle al pago y qué hacer cuando vuelve de pagar.
//
// `activo` es que su plan vende la rutina aparte: a los demás ni se les pregunta.
export function useRutinaPorPagar({ api, activo, alPagar, userId }) {
    const [porPagar, setPorPagar] = useState(null);     // { modalidad, fecha } | null
    const [yendo, setYendo] = useState(false);

    useEffect(() => {
        if (!activo) return;
        let vivo = true;
        api.get('/routines/quiero-la-rutina')
            .then(r => {
                if (vivo) setPorPagar(r.data?.pendiente_de_pago && !confirmando(userId) ? r.data : null);
            })
            .catch(() => {});
        return () => { vivo = false; };
    }, [api, activo, userId]);

    // LA VUELTA DEL PAGO: Stripe devuelve a /dashboard?rutina=ok&session_id=... Se le dice al
    // servidor que mire esa sesión (por si el aviso de Stripe aún no ha llegado) y se recarga
    // su perfil, que es lo que le abre la pantalla de Rutina.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('rutina') !== 'ok') return;
        const sesion = params.get('session_id');
        // Se quita de la barra: recargar no puede volver a sincronizar.
        window.history.replaceState({}, '', window.location.pathname);
        // El `{CHECKOUT_SESSION_ID}` sin sustituir significa que no venimos de Stripe.
        if (!sesion || sesion.includes('{')) return;
        // Mientras se confirma no se le ofrece pagar otra vez.
        escribirLocal(CLAVE_CONFIRMANDO, userId, String(Date.now()));
        setPorPagar(null);
        api.post('/billing/checkout-session/sync', { session_id: sesion })
            .then(r => {
                if (r.data?.payment_status === 'paid') {
                    escribirLocal(CLAVE_CONFIRMANDO, userId, '0');
                    toast.success('Pago hecho. Ya tienes tu rutina en la pantalla de Rutina.');
                } else {
                    toast('Estamos confirmando tu pago. En unos minutos la tendrás en Rutina.');
                }
                if (alPagar) alPagar();
            })
            .catch(e => {
                console.error('[rutina del mes: vuelta del pago]', e?.response?.data || e);
                toast('Estamos confirmando tu pago. Si ya pagaste, no hace falta que pagues otra vez.');
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const pagar = useCallback(async () => {
        if (!porPagar || yendo) return;
        setYendo(true);
        try {
            const r = await api.post('/billing/rutina-del-mes/checkout', {
                modalidad: porPagar.modalidad === 'avanzada' ? 'avanzada' : 'basica',
                success_path: '/dashboard?rutina=ok',
                cancel_path: '/dashboard',
            });
            if (r.data?.checkout_url) { window.location.href = r.data.checkout_url; return; }
            // Cuenta de pruebas: no pasa por Stripe.
            setPorPagar(null);
            toast.success(r.data?.mensaje || 'Hecho.');
            if (alPagar) alPagar();
        } catch (e) {
            console.error('[rutina del mes: ir al pago]', e?.response?.data || e);
            toast.error(mensajeDeError(e, 'No hemos podido abrir el pago. Inténtalo de nuevo en un momento.'));
        } finally {
            setYendo(false);
        }
    }, [api, porPagar, yendo, alPagar]);

    return { porPagar, yendo, pagar };
}
